import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import dayjs from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { BbTaskClaimEntry, bbGetTaskClaims } from '../../../services/blindBoxService';

export function SectionTaskClaims({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const [rows, setRows] = useState<BbTaskClaimEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [userId, setUserId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState<dayjs.Dayjs | null>(null);
  const [to, setTo] = useState<dayjs.Dayjs | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const load = useCallback(() => {
    bbGetTaskClaims({
      userId: userId || undefined,
      taskId: taskId ? parseInt(taskId, 10) || undefined : undefined,
      status: status || undefined,
      from: from?.toISOString() || undefined,
      to: to?.toISOString() || undefined,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    })
      .then((r) => {
        setRows(r.entries || []);
        setTotal(r.total || 0);
      })
      .catch((e: any) => notify(e?.message || 'Error', { type: 'error' }));
  }, [userId, taskId, status, from, to, rowsPerPage, page, notify]);

  useEffect(() => {
    setPage(0);
  }, [userId, taskId, status, from, to]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField size="small" label="userId" value={userId} onChange={(e) => setUserId(e.target.value)} sx={{ minWidth: 280 }} />
        <TextField size="small" label="taskId" value={taskId} onChange={(e) => setTaskId(e.target.value)} sx={{ minWidth: 120 }} />
        <TextField size="small" label="status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }} />
        <DateTimePicker
          label="from"
          value={from}
          onChange={setFrom}
          ampm={false}
          format="YYYY/MM/DD HH:mm"
          slotProps={{ field: { clearable: true }, textField: { size: 'small', sx: { minWidth: 220 } } }}
        />
        <DateTimePicker
          label="to"
          value={to}
          onChange={setTo}
          ampm={false}
          format="YYYY/MM/DD HH:mm"
          slotProps={{ field: { clearable: true }, textField: { size: 'small', sx: { minWidth: 220 } } }}
        />
        <Button onClick={load}>{t('module.blindBox.refresh')}</Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {/*<TableCell>id</TableCell>*/}
              <TableCell>userId</TableCell>
              <TableCell>taskId</TableCell>
              <TableCell>taskName</TableCell>
              <TableCell>taskSlug</TableCell>
              <TableCell>status</TableCell>
              <TableCell>progress</TableCell>
              <TableCell>claimedAt</TableCell>
              <TableCell>resetDate</TableCell>
              <TableCell>createdAt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                {/*<TableCell>{r.id}</TableCell>*/}
                <TableCell>{r.userId}</TableCell>
                <TableCell>{r.taskId}</TableCell>
                <TableCell>{r.taskName}</TableCell>
                <TableCell>{r.taskSlug}</TableCell>
                <TableCell>{r.status}</TableCell>
                <TableCell>{r.progress ?? '—'}</TableCell>
                <TableCell>{r.claimedAt ? dayjs(r.claimedAt).format('YYYY-MM-DD HH:mm:ss') : '—'}</TableCell>
                <TableCell>{r.resetDate ?? '—'}</TableCell>
                <TableCell>{r.createdAt ? dayjs(r.createdAt).format('YYYY-MM-DD HH:mm:ss') : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          const next = parseInt(e.target.value, 10);
          setRowsPerPage(Number.isFinite(next) && next > 0 ? next : 50);
          setPage(0);
        }}
        rowsPerPageOptions={[20, 50, 100]}
      />
    </Stack>
  );
}

